<template>
  <div class="Modal" v-show="show" :class="{ warn }">
    <div class="overlay" @click="$emit('close')">
      <div class="container" @click.stop>
        <ModalClose class="close-icon" @click="$emit('close')" />
        <div class="ModalContents">
          <ModalWarning v-if="warn" class="warn-icon" />
          <div v-if="!!title" class="ModalTitle">{{ title }}</div>
          <div v-if="hasBody" class="ModalBody" ref="modalBodyRef">
            <slot name="body" />
          </div>
        </div>
        <div class="ModalFooter">
          <slot name="footer" />
        </div>
      </div>
    </div>
  </div>
</template>
  
<script lang="ts">
import { defineComponent, computed, useSlots, watch, onBeforeUnmount, ref, nextTick } from 'vue'
import { ModalClose, ModalWarning } from '@ka-nabellinc/ka-nabell-icons'

export default defineComponent({
  components: {
    ModalClose,
    ModalWarning,
  },
  props: {
    show: {
      type: Boolean,
      default: false,
    },
    title: {
      type: String,
      default: null,
    },
    warn: {
      type: Boolean,
      default: false,
    }
  },
  emits: ['close'],
  setup(props) {
    const slots = useSlots()
    const hasBody = computed<boolean>(() => !!slots.body)
    const modalBodyRef = ref<HTMLDivElement>()

    watch(() => props.show, (newVal, oldVal) => {
      // Modal展開時に最上部に自動スクロール
      if (!oldVal && newVal) {
        nextTick(() => {
          modalBodyRef.value?.scroll(0, 0)
        })
      }

      // NOTE: 背景がスクロールされるのを防ぐために、モーダルが表示されている間はbodyのoverflowをhiddenにする
      if (typeof document !== 'undefined') {
        if (newVal) {
          document.body.style.overflow = 'hidden';
        } else {
          document.body.style.overflow = '';
        }
      }
    },{ immediate: true });

    // NOTE: アンマウント時にstyleをリセットする
    onBeforeUnmount(() => {
      document.body.style.overflow = '';
    });

    return {
      hasBody,
      modalBodyRef,
    }
  }
})
</script>

<style scoped>
.Modal {
  height: 100%;
  width: 100%;
  position: relative;
}

.overlay {
  width: 100%;
  height: 100%;
  z-index: 1000;
  position: fixed;
  top: 0;
  left: 0;
  background: var(--primitive-colors-neutral-black-300a);
}

.container {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 28px;
  max-width: 332px;
  width: 90%;
  max-height: 80%;
  height: auto;
  padding: 28px 20px;
  background: var(--primitive-colors-neutral-white-400);
  z-index: 10000;
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  box-shadow: var(--elevation-shadow-300);
  border-radius: 12px;
  box-sizing: border-box;
}

.Modal.warn .container {
  padding: 20px;
}

.ModalContents {
  display: flex;
  width: 100%;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  min-height: 100%;
}

.ModalTitle {
  margin: 0;
  width: 100%;
  text-align: left;
  color: var(--semantic-colors-text-black);
  font-size: var(--font-size-sub-headline-1);
  font-weight: 700;
  line-height: 150%;
  letter-spacing: 0.32px;
}

.close-icon {
  position: absolute;
  top: 8px;
  right: 8px;
  cursor: pointer;
}

.ModalBody {
  color: #000;
  font-size: var(--font-size-capture-1);
  font-weight: 400;
  line-height: 150%;
  letter-spacing: 0.12px;
  width: 100%;
  overflow-y: scroll;
  -ms-overflow-style: none;
  scrollbar-width: none;
}

.ModalBody::-webkit-scrollbar{
  display: none;
}

.ModalFooter {
  display: flex;
  width: 100%;
  justify-content: center;
  align-items: center;
  gap: 4px;
  box-sizing: border-box;
}

.warn-icon {
  width: 56px;
  min-height: 56px;
}
</style>
